import { Link } from 'react-router-dom';
import { Activity, Target, Trophy, ClipboardCheck, BookOpen, Eye, AlertTriangle, Sparkles } from '../icons';
import type { ClientProgressActivity, ClientProgressInsight } from '../../lib/coach-dashboard';

interface Props {
  activities: ClientProgressActivity[];
  attention: ClientProgressInsight[];
  celebrate: ClientProgressInsight[];
}

const TYPE_CONFIG = {
  check_in: { icon: Activity, color: 'bg-sage-100 text-sage-600', label: 'Check-in' },
  goal_completed: { icon: Target, color: 'bg-blue-100 text-blue-600', label: 'Goal' },
  milestone: { icon: Trophy, color: 'bg-yellow-100 text-yellow-600', label: 'Milestone' },
  task_done: { icon: ClipboardCheck, color: 'bg-amber-100 text-amber-600', label: 'Task' },
  journal: { icon: BookOpen, color: 'bg-coral-100 text-coral-600', label: 'Journal' },
} as const;

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function InsightList({
  items,
  emptyMessage,
  tone,
}: {
  items: ClientProgressInsight[];
  emptyMessage: string;
  tone: 'attention' | 'celebrate';
}) {
  const isAttention = tone === 'attention';
  return (
    <div className="p-5">
      <div className="flex items-center gap-3 mb-4">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${isAttention ? 'bg-amber-100 text-amber-700' : 'bg-sage-100 text-sage-700'}`}>
          {isAttention ? <AlertTriangle className="w-5 h-5" /> : <Sparkles className="w-5 h-5" />}
        </div>
        <div>
          <h3 className="text-base font-bold text-chocolate-900">{isAttention ? 'Needs attention' : 'Celebrate progress'}</h3>
          <p className="text-xs text-chocolate-500 mt-0.5">{isAttention ? 'Clients who may need a gentle nudge' : 'Clients building positive momentum'}</p>
        </div>
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-chocolate-400 py-3">{emptyMessage}</p>
      ) : (
        <div className="space-y-3">
          {items.map((item) => (
            <div key={item.userId} className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-cream-200 text-chocolate-700 flex items-center justify-center text-xs font-semibold flex-shrink-0">
                {item.respondentName.charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-chocolate-800 truncate">{item.respondentName}</p>
                <p className="text-xs text-chocolate-500 truncate">{item.detail}</p>
              </div>
              <Link to={`/coach/results/${item.userId}`} className="text-xs text-sage-600 hover:text-sage-700 font-medium whitespace-nowrap">
                View
              </Link>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function CoachClientProgress({ activities, attention, celebrate }: Props) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="freud-card-elevated border border-amber-200 border-l-4 border-l-amber-500">
          <InsightList items={attention} emptyMessage="No clients currently need a follow-up." tone="attention" />
        </div>
        <div className="freud-card-elevated border border-sage-200 border-l-4 border-l-sage-500">
          <InsightList items={celebrate} emptyMessage="No recent celebration signals yet." tone="celebrate" />
        </div>
      </div>

      <div className="freud-card-elevated border-l-4 border-l-chocolate-400">
      <div className="p-6 border-b border-cream-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-chocolate-100 text-chocolate-700 flex items-center justify-center flex-shrink-0">
              <Activity className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-base font-bold text-chocolate-900">Client Progress Activity</h2>
              <p className="text-xs text-chocolate-500 mt-0.5">Last 14 days across all clients</p>
            </div>
          </div>
          <Link
            to="/coach/respondents"
            className="flex items-center gap-1.5 text-sm text-sage-600 hover:text-sage-700 font-medium"
          >
            <Eye className="w-4 h-4" />
            View all
          </Link>
        </div>
      </div>

      <div className="divide-y divide-cream-100">
        {activities.length === 0 ? (
          <div className="px-6 py-10 text-center text-chocolate-400">
            <Activity className="w-8 h-8 mx-auto mb-2 opacity-40" />
            <p className="text-sm">No client activity in the last 14 days.</p>
          </div>
        ) : (
          activities.slice(0, 12).map((a, i) => {
            const cfg = TYPE_CONFIG[a.type];
            const Icon = cfg.icon;
            return (
              <div key={i} className="flex items-center gap-4 px-6 py-3 hover:bg-cream-50 transition-colors">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${cfg.color}`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-chocolate-800 truncate">{a.respondentName}</span>
                    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${cfg.color}`}>{cfg.label}</span>
                  </div>
                  <p className="text-xs text-chocolate-500 truncate">{a.label}</p>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <span className="text-xs text-chocolate-400">{timeAgo(a.occurredAt)}</span>
                  <Link
                    to={`/coach/results/${a.userId}`}
                    className="text-xs text-sage-600 hover:text-sage-700 font-medium whitespace-nowrap"
                  >
                    View
                  </Link>
                </div>
              </div>
            );
          })
        )}
      </div>
      </div>
    </div>
  );
}
